/**
 * ParticipantActivityPanel — Deal Room "Participants" tab (Phase 1).
 *
 * A dedicated full-tab experience for the operational participant
 * directory inside a Deal Room. Replaces the summary-only
 * `ParticipantsLiveCard` (kept in the right-rail) with a richer surface:
 *   - Responsiveness summary header (Active / Slow / Silent counts)
 *   - Per-participant detail card: avatar (with presence dot), name,
 *     role, team badge, responsiveness pill, last-seen, open task count,
 *     last 3 activities sourced from `deal.activity` filtered by actor,
 *     and inline `Chase` + `Message` actions.
 *
 * Calm, premium, light-mode operational surface — matches the
 * Golden Path master design system tokens.
 */
import React, { useMemo, useState } from "react";
import {
  MessageSquare, Send, UserPlus, Sparkles, ArrowRight,
} from "lucide-react";
import { PremiumInput } from "../brand/PremiumInput";
import { LIGHT, cardStyle, initialsOf } from "./RoomFlow";

const _t = (a) => (typeof a === "number" ? a : a ? Date.parse(a) : 0);
const fmtRel = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - _t(iso);
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const RESP_META = {
  active:     { dot: "#10B981", label: "Active",     pulse: true,  text: "#15803D", bg: "#ECFDF5", border: "#A7F3D0" },
  responsive: { dot: "#22C55E", label: "Responsive", pulse: false, text: "#15803D", bg: "#ECFDF5", border: "#A7F3D0" },
  slow:       { dot: "#F59E0B", label: "Slow",       pulse: false, text: "#A16207", bg: "#FFFBEB", border: "#FDE68A" },
  silent:     { dot: "#94A3B8", label: "Silent",     pulse: false, text: "#64748B", bg: "#F1F5F9", border: "#E2E8F0" },
};

// iter-184 P1 — ultra-subtle micro-governance indicators on participant
// avatars. NOT full chips. Thin tonal ring + title tooltip surfaces
// the visibility level on hover/tap. Reads "trustworthy at first
// glance" like Figma collaborator presence + Linear ownership markers.
const VISIBILITY_RING = {
  internal:         { ring: "#A5B4FC", dot: "#6366F1", label: "Internal" },
  full_access:      { ring: "#E2E8F0", dot: "#94A3B8", label: "Full access" },
  solicitor_access: { ring: "#DDD6FE", dot: "#8B5CF6", label: "Solicitor access" },
  buyer_access:     { ring: "#BAE6FD", dot: "#0EA5E9", label: "Buyer access" },
  seller_access:    { ring: "#BBF7D0", dot: "#10B981", label: "Seller access" },
  read_only:        { ring: "#E2E8F0", dot: "#94A3B8", label: "Read only" },
  restricted:       { ring: "#FDE68A", dot: "#D97706", label: "Restricted" },
  observer:         { ring: "#E2E8F0", dot: "#94A3B8", label: "Observer" },
};

const visibilityRing = (visibility) =>
  VISIBILITY_RING[visibility] || VISIBILITY_RING.full_access;


const ResponsivenessPill = ({ responsiveness }) => {
  const meta = RESP_META[responsiveness] || RESP_META.silent;
  return (
    <span
      className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full text-[10px] font-bold uppercase tracking-[0.10em]"
      style={{ background: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ background: meta.dot, boxShadow: meta.pulse ? `0 0 0 3px ${meta.dot}33` : "none" }}
      />
      {meta.label}
    </span>
  );
};

const Stat = ({ label, value, tone = "slate" }) => {
  const colors = {
    slate:   { bg: "#FAFBFD", fg: "#0F172A" },
    violet:  { bg: "#F5F3FF", fg: "#6D28D9" },
    amber:   { bg: "#FFFBEB", fg: "#B45309" },
    emerald: { bg: "#ECFDF5", fg: "#047857" },
  }[tone];
  return (
    <div
      className="flex-1 rounded-lg px-3 py-2 text-center min-w-0"
      style={{ background: colors.bg, border: "1px solid rgba(15,23,42,0.06)" }}
    >
      <p className="text-[9.5px] font-bold uppercase tracking-[0.12em]" style={{ color: LIGHT.textSubtle }}>{label}</p>
      <p className="text-[15px] font-bold tabular-nums mt-0.5" style={{ color: colors.fg }}>{value}</p>
    </div>
  );
};

const ActivityLine = ({ item }) => {
  const text = (item.text || item.kind || "").trim();
  return (
    <li className="flex items-start gap-2 py-1.5">
      <span
        className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
        style={{ background: "#A78BFA" }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] leading-snug truncate" style={{ color: LIGHT.textPrimary }}>{text}</p>
        <p className="text-[10px] mt-0.5" style={{ color: LIGHT.textSubtle }}>{fmtRel(item.at)}</p>
      </div>
    </li>
  );
};

const ParticipantCard = ({ p, idx, activity, awaitingForUser, onChase, onMessage }) => {
  const meta = RESP_META[p.responsiveness] || RESP_META.silent;
  const fullName = p.full_name || p.email || "Participant";
  const role = p.role_label || p.role || "Participant";
  const openTasks = p.assigned_open_tasks || 0;
  const awaitingCount = awaitingForUser?.length || 0;

  const ringHue = (idx * 67) % 360;
  const avatarBg = `linear-gradient(135deg, hsl(${ringHue},48%,58%), hsl(${(ringHue + 50) % 360},48%,38%))`;
  const presenceColor = meta.dot;

  return (
    <article
      className="rounded-2xl p-5 transition-all hover:-translate-y-0.5"
      style={{ ...cardStyle, boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
      data-testid={`participants-tab-card-${p.user_id || p.id || idx}`}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* iter-184 P1 — micro-governance: thin tonal ring + tiny
            access marker reveals the visibility level on hover. */}
        <span
          className="relative inline-flex items-center justify-center rounded-2xl shrink-0"
          style={{
            padding: "2px",
            background: visibilityRing(p.visibility).ring,
          }}
          title={`${visibilityRing(p.visibility).label} access`}
          data-testid={`participant-avatar-visibility-${p.user_id || p.id || idx}`}
          data-visibility={p.visibility || "full_access"}
        >
          <span
            className="relative w-12 h-12 rounded-2xl text-white text-[13px] font-bold flex items-center justify-center"
            style={{ background: avatarBg, boxShadow: "0 6px 14px -6px rgba(15,23,42,0.15), inset 0 1px 0 rgba(255,255,255,0.18)" }}
          >
            {initialsOf(fullName)}
            <span
              aria-hidden="true"
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white"
              style={{ background: presenceColor, boxShadow: meta.pulse ? `0 0 0 4px ${presenceColor}26` : "none" }}
            />
            {/* iter-184 — tiny visibility marker dot at top-right. */}
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white"
              style={{ background: visibilityRing(p.visibility).dot }}
              data-testid={`participant-visibility-dot-${p.user_id || p.id || idx}`}
            />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-[14px] font-bold leading-tight truncate" style={{ color: LIGHT.textPrimary }}>{fullName}</p>
            {p.is_internal && (
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 h-[16px] inline-flex items-center rounded-full"
                    style={{ background: "#F5F3FF", color: "#6D28D9", border: "1px solid #E9D5FF" }}>
                Team
              </span>
            )}
          </div>
          <p className="text-[11.5px] mt-0.5" style={{ color: LIGHT.textMuted }}>{role}</p>
          <p className="text-[10.5px] mt-0.5" style={{ color: LIGHT.textSubtle }}>
            {p.responsiveness === "active" ? "Online now" : `Last seen ${fmtRel(p.last_seen_at)}`}
          </p>
        </div>
        <ResponsivenessPill responsiveness={p.responsiveness} />
      </div>

      {/* Stats row */}
      <div className="flex items-stretch gap-2 mt-4">
        <Stat label="Open tasks" value={openTasks} tone={openTasks > 0 ? "amber" : "slate"} />
        <Stat label="Awaiting" value={awaitingCount} tone={awaitingCount > 0 ? "violet" : "slate"} />
        <Stat label="Activity" value={activity.length} tone="slate" />
      </div>

      {/* Recent activity */}
      {activity.length > 0 ? (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: LIGHT.textSubtle }}>
            Recent activity
          </p>
          <ul className="space-y-0">
            {activity.slice(0, 3).map((a, i) => (
              <ActivityLine key={`${a.id || a.at}-${i}`} item={a} />
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-4 rounded-lg px-3 py-2.5"
             style={{ background: "#FAFBFD", border: "1px dashed rgba(15,23,42,0.10)" }}>
          <p className="text-[11px]" style={{ color: LIGHT.textSubtle }}>No recent activity for this participant.</p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2 pt-3" style={{ borderTop: "1px solid rgba(15,23,42,0.06)" }}>
        <button
          type="button"
          onClick={() => onMessage?.(p)}
          className="flex-1 h-9 rounded-lg text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-all"
          style={{ background: "#FFFFFF", color: LIGHT.textPrimary, border: "1px solid rgba(15,23,42,0.06)" }}
          data-testid={`participants-tab-message-${p.user_id || p.id || idx}`}
        >
          <MessageSquare size={11} /> Message
        </button>
        <button
          type="button"
          onClick={() => onChase?.(p)}
          disabled={p.responsiveness === "active"}
          className="flex-1 h-9 rounded-lg text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          style={
            p.responsiveness === "active"
              ? { background: "#F1F5F9", color: LIGHT.textMuted, border: "1px solid rgba(15,23,42,0.06)" }
              : { background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)", color: "#FFFFFF", boxShadow: "0 8px 18px -8px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)" }
          }
          data-testid={`participants-tab-chase-${p.user_id || p.id || idx}`}
        >
          <Send size={11} /> Chase
        </button>
      </div>
    </article>
  );
};

const SummaryHeader = ({ participants, onInvite }) => {
  const counts = useMemo(() => {
    const c = { active: 0, responsive: 0, slow: 0, silent: 0 };
    participants.forEach((p) => { c[p.responsiveness || "silent"] = (c[p.responsiveness || "silent"] || 0) + 1; });
    return c;
  }, [participants]);

  const total = participants.length;
  const activePct = total > 0 ? Math.round(((counts.active + counts.responsive) / total) * 100) : 0;

  return (
    <header
      className="rounded-2xl p-5"
      style={{ ...cardStyle, boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
      data-testid="participants-tab-summary"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "#6D28D9" }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle cf-pulse" style={{ background: "#7C3AED" }} />
            Participants · Responsiveness
          </p>
          <h2 className="font-display text-[20px] font-bold leading-tight mt-2" style={{ color: LIGHT.textPrimary }}>
            <span className="tabular-nums">{total}</span>
            <span className="text-[14px] font-semibold ml-2" style={{ color: LIGHT.textMuted }}>
              {total === 1 ? "person in this deal" : "people in this deal"}
            </span>
          </h2>
          <p className="text-[12.5px] mt-2 max-w-md" style={{ color: LIGHT.textMuted }}>
            <span className="font-bold" style={{ color: LIGHT.textPrimary }}>{activePct}%</span> responding healthily.
            {counts.silent > 0 && (
              <> <span style={{ color: "#B45309" }}>{counts.silent} silent — consider a chase.</span></>
            )}
          </p>
        </div>
        {onInvite && (
          <button
            type="button"
            onClick={onInvite}
            className="h-10 px-4 rounded-xl text-[12.5px] font-semibold inline-flex items-center gap-1.5 gp-btn-primary"
            data-testid="participants-tab-invite"
          >
            <UserPlus size={13} /> Invite participant
          </button>
        )}
      </div>

      {/* Responsiveness mini-bar */}
      {total > 0 && (
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full overflow-hidden flex" style={{ background: "#F1F5F9" }}>
            {counts.active > 0 && <span style={{ width: `${(counts.active / total) * 100}%`, background: RESP_META.active.dot }} />}
            {counts.responsive > 0 && <span style={{ width: `${(counts.responsive / total) * 100}%`, background: RESP_META.responsive.dot }} />}
            {counts.slow > 0 && <span style={{ width: `${(counts.slow / total) * 100}%`, background: RESP_META.slow.dot }} />}
            {counts.silent > 0 && <span style={{ width: `${(counts.silent / total) * 100}%`, background: RESP_META.silent.dot }} />}
          </div>
          <div className="flex items-center gap-3 text-[10.5px] font-semibold flex-wrap" style={{ color: LIGHT.textMuted }}>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: RESP_META.active.dot }} /> {counts.active} active</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: RESP_META.responsive.dot }} /> {counts.responsive} responsive</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: RESP_META.slow.dot }} /> {counts.slow} slow</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: RESP_META.silent.dot }} /> {counts.silent} silent</span>
          </div>
        </div>
      )}
    </header>
  );
};

const matchKey = (p) => (p.user_id || p.id || p.email || "").toString().toLowerCase();
const matchKeyName = (p) => (p.full_name || p.email || "").toLowerCase();

export const ParticipantActivityPanel = ({
  participants = [],
  activity = [],
  awaiting = [],
  onInvite,
  onChase,
  onMessage,
}) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  // Filter participants
  const filtered = useMemo(() => {
    let list = participants;
    if (filter !== "all") list = list.filter((p) => (p.responsiveness || "silent") === filter);
    if (q.trim()) {
      const lo = q.toLowerCase();
      list = list.filter((p) =>
        matchKeyName(p).includes(lo) ||
        (p.role_label || p.role || "").toLowerCase().includes(lo)
      );
    }
    return list;
  }, [participants, filter, q]);

  // Group activity per participant (best-effort match on actor name / id)
  const activityByActor = useMemo(() => {
    const map = {};
    activity.forEach((a) => {
      const actor = (a.actor || "").toString().toLowerCase();
      if (!actor) return;
      if (!map[actor]) map[actor] = [];
      map[actor].push(a);
    });
    Object.values(map).forEach((arr) => arr.sort((x, y) => _t(y.at) - _t(x.at)));
    return map;
  }, [activity]);

  // Group awaiting items per owner
  const awaitingByOwner = useMemo(() => {
    const map = {};
    awaiting.forEach((it) => {
      const owner = (it.owner || "").toString().toLowerCase();
      if (!owner) return;
      if (!map[owner]) map[owner] = [];
      map[owner].push(it);
    });
    return map;
  }, [awaiting]);

  const getActivity = (p) => {
    const candidates = [matchKey(p), matchKeyName(p)].filter(Boolean);
    for (const k of candidates) {
      if (activityByActor[k]) return activityByActor[k];
    }
    return [];
  };
  const getAwaiting = (p) => {
    const candidates = [matchKeyName(p), (p.role_label || p.role || "").toLowerCase()].filter(Boolean);
    for (const k of candidates) {
      if (awaitingByOwner[k]) return awaitingByOwner[k];
    }
    return [];
  };

  const FILTER_OPTIONS = [
    { id: "all", label: "Everyone", count: participants.length },
    { id: "active", label: "Active", count: participants.filter((p) => p.responsiveness === "active").length },
    { id: "responsive", label: "Responsive", count: participants.filter((p) => p.responsiveness === "responsive").length },
    { id: "slow", label: "Slow", count: participants.filter((p) => p.responsiveness === "slow").length },
    { id: "silent", label: "Silent", count: participants.filter((p) => p.responsiveness === "silent").length },
  ];

  if (participants.length === 0) {
    return (
      <div
        className="rounded-2xl p-12 text-center"
        style={{ ...cardStyle, boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
        data-testid="participants-tab-empty"
      >
        <div
          className="w-14 h-14 rounded-2xl inline-flex items-center justify-center mb-3"
          style={{ background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)", boxShadow: "0 14px 28px -10px rgba(99,102,241,0.45)" }}
        >
          <Sparkles size={22} className="text-white" />
        </div>
        <p className="font-display text-[18px] font-bold" style={{ color: LIGHT.textPrimary }}>No participants yet</p>
        <p className="text-[12.5px] mt-1.5 max-w-md mx-auto" style={{ color: LIGHT.textMuted }}>
          Invite buyers, sellers, solicitors and brokers to get the operational thread moving.
        </p>
        {onInvite && (
          <button
            type="button"
            onClick={onInvite}
            className="mt-5 h-10 px-4 rounded-xl text-[12.5px] font-semibold inline-flex items-center gap-1.5 gp-btn-primary"
            data-testid="participants-tab-empty-invite"
          >
            <UserPlus size={13} /> Invite first participant <ArrowRight size={11} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="participants-tab-panel">
      <SummaryHeader participants={participants} onInvite={onInvite} />

      {/* Filter + search bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white overflow-x-auto cf-scroll"
             style={{ border: "1px solid rgba(15,23,42,0.06)" }}>
          {FILTER_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setFilter(o.id)}
              className={`px-3 h-8 rounded-lg text-[11.5px] font-semibold transition-all inline-flex items-center gap-1.5 shrink-0 ${
                filter === o.id ? "text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              style={filter === o.id ? { background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)", boxShadow: "0 6px 14px -6px rgba(99,102,241,0.45)" } : {}}
              data-testid={`participants-tab-filter-${o.id}`}
            >
              {o.label}
              <span
                className="text-[9.5px] font-bold tabular-nums px-1.5 h-[16px] inline-flex items-center rounded-full"
                style={filter === o.id
                  ? { background: "rgba(255,255,255,0.20)", color: "#FFFFFF" }
                  : { background: "#F1F5F9", color: "#475569" }}
              >
                {o.count}
              </span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-md ml-auto">
          <PremiumInput
            value={q}
            onChange={setQ}
            placeholder="Search name or role…"
            format="search"
            size="sm"
            clearable
            testid="participants-tab-search"
          />
        </div>
      </div>

      {/* Cards grid */}
      {filtered.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed p-12 text-center text-[13px]"
          style={{ borderColor: "rgba(15,23,42,0.10)", color: LIGHT.textMuted }}
          data-testid="participants-tab-no-match"
        >
          No participants match the current filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((p, i) => (
            <ParticipantCard
              key={p.user_id || p.id || p.email || i}
              p={p}
              idx={i}
              activity={getActivity(p)}
              awaitingForUser={getAwaiting(p)}
              onChase={onChase}
              onMessage={onMessage}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ParticipantActivityPanel;
